import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { ReferenceMeasureLength } from "@domain/project/floorplan/reference-measure-length";
import { Exclude, Expose } from "class-transformer";
import { IsNumber, IsOptional, IsString } from "class-validator";
import { Subject } from "rxjs";
import { v4 as uuidv4 } from "uuid";

export class Pipeline extends FloorplanItem implements ReferenceMeasureLength {
  @IsNumber()
  @Expose({ name: "width" })
  private _width: number;

  @IsNumber()
  @Expose({ name: "height" })
  private _height: number;

  @IsNumber()
  @Expose({ name: "rotation" })
  private _rotation: number;

  @IsNumber()
  @IsOptional()
  @Expose({ name: "referenceLength" })
  public _referenceLength?: number;

  @IsString()
  @IsOptional()
  public diameter?: string;

  @IsString()
  @IsOptional()
  public medium?: string;

  @IsString()
  @IsOptional()
  public temperature?: string;

  @IsString()
  @IsOptional()
  public material?: string;

  @IsString()
  @IsOptional()
  public name?: string;

  @Exclude()
  public lengthChanged: Subject<{ length: number }>;

  @Exclude()
  referenceLengthChanged: Subject<{ referenceLength: number | undefined }>;

  constructor(
    floorplan: Floorplan,
    id: string,
    x: number,
    y: number,
    width: number,
    height: number,
    diameter?: string,
    medium?: string,
    temperature?: string,
    material?: string,
    rotation: number = 0,
    name?: string,
  ) {
    super(floorplan, id, x, y);
    this._width = width;
    this._height = height;
    this._rotation = rotation;
    this.diameter = diameter;
    this.medium = medium;
    this.temperature = temperature;
    this.material = material;
    this.name = name;
    this.lengthChanged = new Subject();
    this.referenceLengthChanged = new Subject();
    this.updateReferenceLength(this._width);
  }

  get width(): number {
    return this._width;
  }

  get height(): number {
    return this._height;
  }

  get rotation(): number {
    return this._rotation;
  }

  get referenceLength(): number | undefined {
    return this._referenceLength;
  }

  updateMetadata(diameter?: string, medium?: string, temperature?: string, material?: string, name?: string) {
    this.diameter = diameter;
    this.medium = medium;
    this.temperature = temperature;
    this.material = material;
    this.name = name;
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_UPDATED, this);
  }

  updateLength(referenceLength: number) {
    this._width = referenceLength * this._floorplan.getReferenceScale();
    this.updateForm(this._width, this._height, this._rotation, this.x, this.y);
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_SIZE_CHANGED_BY_INPUT, this);
  }

  updateReferenceLength(length: number) {
    if (this.isReferenceMeasureSet()) {
      this._referenceLength = Math.round((length / this._floorplan.getReferenceScale()) * 100) / 100;
    } else {
      this._referenceLength = undefined;
    }
  }

  updateForm(width: number, height: number, rotation: number, x: number, y: number) {
    this.lengthChanged.next({ length: width });
    this.updateReferenceLength(width);
    this._width = width;
    this._height = height;
    this._rotation = rotation;
    this.updatePosition(x, y);
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.pipelinesItemTextVisible;
  }

  isReferenceMeasureSet() {
    if (this._floorplan) return !!this._floorplan.referenceMeasure;
    return false;
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanItem) | undefined {
    const diameter = this.diameter;
    const height = this.height;
    const material = this.material;
    const medium = this.medium;
    const name = this.name;
    const rotation = this.rotation;
    const temperature = this.temperature;
    const width = this.width;

    return (x: number, y: number) =>
      Pipeline.create(this._floorplan, x, y, width, height, diameter, medium, temperature, material, rotation, name);
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.pipelines;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as Pipeline,
        this._floorplan.pipelines,
        FloorplanEventType.PIPELINE_ADDED,
        () => this._floorplan.project.setPipelinePositionIds(),
        afterDuplicateAdded as ((duplicate: Pipeline) => void) | undefined,
      );
  }

  override delete() {
    this.lengthChanged.unsubscribe();
    this._floorplan.deletePipeline(this);
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshPipelinesLock();
  }

  static create(
    floorplan: Floorplan,
    x: number,
    y: number,
    width: number,
    height: number,
    diameter?: string,
    medium?: string,
    temperature?: string,
    material?: string,
    rotation: number = 0,
    name?: string,
  ) {
    return new Pipeline(floorplan, uuidv4(), x, y, width, height, diameter, medium, temperature, material, rotation, name);
  }
}
